// Seed data for APN development and testing
import {
  collection,
  doc,
  setDoc,
  addDoc,
  deleteDoc,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { db } from '../firebase';

// ============ SAMPLE USERS ============
export const sampleUsers = [
  {
    id: 'seeded-user-1',
    firstName: 'Marcus',
    lastName: 'Thompson',
    displayName: 'Marcus Thompson',
    email: 'marcus.thompson@demo-apn.com',
    company: 'J.P. Morgan',
    jobTitle: 'Vice President, Investment Banking',
    bio: 'Seasoned IB professional with 8+ years in tech M&A. Previously at Lazard. Georgetown MBA.',
    location: 'New York, NY',
    linkedinUrl: '',
    website: '',
    status: 'approved',
    role: 'member',
    membershipTier: 'professional',
    profileVisibility: 'members',
    showEventRegistrations: true,
    emailNotifications: true,
    eventReminders: true,
    newsletterSubscription: true,
    isSeeded: true
  },
  {
    id: 'seeded-user-2',
    firstName: 'Priya',
    lastName: 'Sharma',
    displayName: 'Priya Sharma',
    email: 'priya.sharma@demo-apn.com',
    company: 'Blackstone',
    jobTitle: 'Associate, Private Equity',
    bio: 'PE associate focused on healthcare and consumer sectors. Former analyst at Morgan Stanley. Wharton alum.',
    location: 'New York, NY',
    linkedinUrl: '',
    website: '',
    status: 'approved',
    role: 'member',
    membershipTier: 'professional',
    profileVisibility: 'members',
    showEventRegistrations: true,
    emailNotifications: true,
    eventReminders: true,
    newsletterSubscription: true,
    isSeeded: true
  },
  {
    id: 'seeded-user-3',
    firstName: 'David',
    lastName: 'Chen',
    displayName: 'David Chen',
    email: 'david.chen@demo-apn.com',
    company: 'Deloitte',
    jobTitle: 'Senior Manager, M&A Advisory',
    bio: 'M&A advisory lead specializing in cross-border transactions. CFA charterholder. 10+ years in deal advisory.',
    location: 'Chicago, IL',
    linkedinUrl: '',
    website: '',
    status: 'approved',
    role: 'member',
    membershipTier: 'executive',
    profileVisibility: 'members',
    showEventRegistrations: true,
    emailNotifications: true,
    eventReminders: true,
    newsletterSubscription: true,
    isSeeded: true
  },
  {
    id: 'seeded-user-4',
    firstName: 'Jasmine',
    lastName: 'Williams',
    displayName: 'Jasmine Williams',
    email: 'jasmine.williams@demo-apn.com',
    company: 'Goldman Sachs',
    jobTitle: 'Analyst, Equity Research',
    bio: 'Equity research analyst covering fintech and payments. Howard University grad. Passionate about diversity in finance.',
    location: 'San Francisco, CA',
    linkedinUrl: '',
    website: '',
    status: 'approved',
    role: 'member',
    membershipTier: 'standard',
    profileVisibility: 'members',
    showEventRegistrations: true,
    emailNotifications: true,
    eventReminders: true,
    newsletterSubscription: true,
    isSeeded: true
  },
  {
    id: 'seeded-user-5',
    firstName: 'Robert',
    lastName: 'Martinez',
    displayName: 'Robert Martinez',
    email: 'robert.martinez@demo-apn.com',
    company: 'Evercore',
    jobTitle: 'Director, Restructuring',
    bio: 'Restructuring expert with 12+ years advising distressed companies. Army veteran. Columbia Law & MBA.',
    location: 'Dallas, TX',
    linkedinUrl: '',
    website: '',
    status: 'approved',
    role: 'member',
    membershipTier: 'professional',
    profileVisibility: 'members',
    showEventRegistrations: true,
    emailNotifications: true,
    eventReminders: true,
    newsletterSubscription: true,
    isSeeded: true
  }
];

// Sample groups data
export const sampleGroups = [
  {
    id: '1',
    name: 'Investment Banking Professionals',
    description: 'A community for investment banking analysts, associates, and VPs to share insights, network, and discuss industry trends.',
    category: 'professional',
    memberCount: 5,
    isPrivate: false,
    isActive: true,
    members: ['seeded-user-1', 'seeded-user-2', 'seeded-user-3', 'seeded-user-4', 'seeded-user-5'],
    moderators: ['seeded-user-1'],
    tags: ['investment-banking', 'finance', 'networking'],
    isSeeded: true
  },
  {
    id: '2',
    name: 'Private Equity Network',
    description: 'Connect with PE professionals, discuss deal flow, and share market insights.',
    category: 'professional',
    memberCount: 3,
    isPrivate: true,
    isActive: true,
    members: ['seeded-user-2', 'seeded-user-3', 'seeded-user-5'],
    moderators: ['seeded-user-2'],
    tags: ['private-equity', 'deals', 'networking'],
    isSeeded: true
  },
  {
    id: '3',
    name: 'M&A Specialists',
    description: 'For professionals involved in mergers and acquisitions. Share deal experiences and best practices.',
    category: 'professional',
    memberCount: 4,
    isPrivate: false,
    isActive: true,
    members: ['seeded-user-1', 'seeded-user-3', 'seeded-user-4', 'seeded-user-5'],
    moderators: ['seeded-user-3'],
    tags: ['mergers', 'acquisitions', 'deals'],
    isSeeded: true
  },
  {
    id: '4',
    name: 'New York Finance Professionals',
    description: 'Local networking group for finance professionals in the New York area.',
    category: 'location',
    memberCount: 3,
    isPrivate: false,
    isActive: true,
    members: ['seeded-user-1', 'seeded-user-2', 'seeded-user-4'],
    moderators: ['seeded-user-1'],
    tags: ['new-york', 'local', 'networking'],
    isSeeded: true
  },
  {
    id: '5',
    name: 'Veterans in Finance',
    description: 'Supporting military veterans transitioning to and advancing in finance careers.',
    category: 'interest',
    memberCount: 2,
    isPrivate: false,
    isActive: true,
    members: ['seeded-user-5', 'seeded-user-3'],
    moderators: ['seeded-user-5'],
    tags: ['veterans', 'military', 'career-transition'],
    isSeeded: true
  }
];

// Sample group posts
export const sampleGroupPosts = [
  {
    id: 'seeded-post-1',
    groupId: '1',
    title: 'Market Outlook for Q1 2026',
    content: 'What are everyone\'s thoughts on the market outlook heading into Q1? The tech IPO pipeline looks strong — seeing several unicorns gearing up. Anyone tracking the fintech valuations closely?',
    authorId: 'seeded-user-1',
    authorName: 'Marcus Thompson',
    likes: ['seeded-user-2', 'seeded-user-4'],
    likeCount: 2,
    commentCount: 2,
    views: 18,
    replies: [],
    isSeeded: true
  },
  {
    id: 'seeded-post-2',
    groupId: '1',
    title: 'Best Practices for Client Presentations',
    content: 'Looking for advice on creating compelling pitch decks. We\'ve been using a new framework that ties valuation narratives to market comps more effectively. What tools and frameworks do you all use?',
    authorId: 'seeded-user-4',
    authorName: 'Jasmine Williams',
    likes: ['seeded-user-1', 'seeded-user-3', 'seeded-user-5'],
    likeCount: 3,
    commentCount: 1,
    views: 24,
    replies: [],
    isSeeded: true
  },
  {
    id: 'seeded-post-3',
    groupId: '2',
    title: 'Deal Sourcing in Healthcare PE',
    content: 'We\'ve been seeing strong deal flow in healthcare services, especially behavioral health platforms. Anyone else noticing consolidation plays in this space? Would love to compare notes.',
    authorId: 'seeded-user-2',
    authorName: 'Priya Sharma',
    likes: ['seeded-user-3'],
    likeCount: 1,
    commentCount: 1,
    views: 12,
    replies: [],
    isSeeded: true
  },
  {
    id: 'seeded-post-4',
    groupId: '3',
    title: 'Cross-Border M&A: Navigating Regulatory Hurdles',
    content: 'Just closed a complex cross-border deal between a US acquirer and a European target. The regulatory landscape has shifted significantly. Happy to share lessons learned if there\'s interest.',
    authorId: 'seeded-user-3',
    authorName: 'David Chen',
    likes: ['seeded-user-1', 'seeded-user-5'],
    likeCount: 2,
    commentCount: 2,
    views: 31,
    replies: [],
    isSeeded: true
  }
];

// Sample group post comments
export const sampleGroupComments = [
  {
    postId: 'seeded-post-1',
    content: 'Great points, Marcus. We\'re seeing similar trends on the equity research side. Fintech valuations are compressing but deal volume is actually picking up.',
    authorId: 'seeded-user-4',
    authorName: 'Jasmine Williams',
    likes: ['seeded-user-1'],
    likeCount: 1,
    isSeeded: true
  },
  {
    postId: 'seeded-post-1',
    content: 'Agree — the IPO window is opening back up. We had two mandates come in just last week. Definitely feels like momentum is building.',
    authorId: 'seeded-user-3',
    authorName: 'David Chen',
    likes: ['seeded-user-1', 'seeded-user-2'],
    likeCount: 2,
    isSeeded: true
  },
  {
    postId: 'seeded-post-2',
    content: 'We switched to a market-comp-first approach last year and it made a huge difference with clients. Happy to share our template if anyone\'s interested — DM me.',
    authorId: 'seeded-user-1',
    authorName: 'Marcus Thompson',
    likes: ['seeded-user-4'],
    likeCount: 1,
    isSeeded: true
  },
  {
    postId: 'seeded-post-3',
    content: 'Behavioral health is definitely hot right now. We\'re tracking 3-4 platform opportunities in that space. The multiples have been surprisingly resilient.',
    authorId: 'seeded-user-5',
    authorName: 'Robert Martinez',
    likes: ['seeded-user-2'],
    likeCount: 1,
    isSeeded: true
  },
  {
    postId: 'seeded-post-4',
    content: 'Would love to hear more about the regulatory side, David. We ran into CFIUS issues on our last cross-border deal and it nearly killed the timeline.',
    authorId: 'seeded-user-1',
    authorName: 'Marcus Thompson',
    likes: ['seeded-user-3', 'seeded-user-5'],
    likeCount: 2,
    isSeeded: true
  },
  {
    postId: 'seeded-post-4',
    content: 'This is extremely relevant. The EU\'s new foreign subsidies regulation is adding another layer of complexity. Definitely interested in your takeaways.',
    authorId: 'seeded-user-2',
    authorName: 'Priya Sharma',
    likes: ['seeded-user-3'],
    likeCount: 1,
    isSeeded: true
  }
];

// Sample jobs
export const sampleJobs = [
  {
    title: 'Investment Banking Analyst — TMT Coverage',
    company: 'Goldman Sachs',
    location: 'New York, NY',
    type: 'Full-time',
    experience: 'Entry Level',
    salary: '$150,000 - $180,000',
    description: 'Join our TMT Investment Banking division as an Analyst. You will work on M&A transactions, IPOs, and other corporate finance activities for technology, media, and telecom clients.',
    requirements: [
      'Bachelor\'s degree in Finance, Economics, or related field',
      'Strong analytical and quantitative skills',
      'Proficiency in Excel and PowerPoint',
      'Excellent communication skills'
    ],
    benefits: [
      'Competitive salary and bonus',
      'Health insurance',
      '401(k) matching',
      'Professional development opportunities'
    ],
    postedBy: 'seeded-user-4',
    postedByName: 'Jasmine Williams',
    applications: [
      { userId: 'seeded-user-1', name: 'Marcus Thompson', appliedAt: '2026-02-15T10:00:00Z', status: 'pending' }
    ],
    views: 42,
    status: 'active',
    isActive: true,
    isSeeded: true
  },
  {
    title: 'Private Equity Associate — Healthcare',
    company: 'Blackstone',
    location: 'New York, NY',
    type: 'Full-time',
    experience: 'Mid Level',
    salary: '$200,000 - $275,000',
    description: 'Seeking an experienced Associate to join our Healthcare Private Equity team. Focus on deal sourcing, due diligence, and portfolio company management in healthcare services and life sciences.',
    requirements: [
      'MBA or 3+ years investment banking experience',
      'Strong financial modeling skills',
      'Experience with LBO analysis',
      'Healthcare sector knowledge preferred'
    ],
    benefits: [
      'Competitive compensation package',
      'Carried interest participation',
      'Comprehensive benefits',
      'Travel opportunities'
    ],
    postedBy: 'seeded-user-2',
    postedByName: 'Priya Sharma',
    applications: [],
    views: 67,
    status: 'active',
    isActive: true,
    isSeeded: true
  },
  {
    title: 'Restructuring Director',
    company: 'Evercore',
    location: 'Dallas, TX',
    type: 'Full-time',
    experience: 'Senior Level',
    salary: '$300,000 - $400,000',
    description: 'Lead restructuring engagements for distressed middle-market companies. Manage client relationships, oversee financial analysis, and drive deal execution across various industries.',
    requirements: [
      '8+ years restructuring or distressed advisory experience',
      'CFA, CPA, or CIRA certification preferred',
      'Strong leadership and client management skills',
      'Experience with Chapter 11 and out-of-court restructurings'
    ],
    benefits: [
      'Industry-leading compensation',
      'Performance bonuses',
      'Full benefits package',
      'Flexible work arrangements'
    ],
    postedBy: 'seeded-user-5',
    postedByName: 'Robert Martinez',
    applications: [
      { userId: 'seeded-user-3', name: 'David Chen', appliedAt: '2026-02-20T14:30:00Z', status: 'pending' }
    ],
    views: 35,
    status: 'active',
    isActive: true,
    isSeeded: true
  }
];

// Sample projects
export const sampleProjects = [
  {
    title: 'Tax Advisory Needed',
    company: 'Mid-Market Private Company',
    duration: '3-5 weeks',
    type: 'Contract',
    description: 'Seeking an experienced tax advisory professional to assist with corporate tax planning, compliance review, and strategic tax optimization for a mid-market company undergoing restructuring. Must have deep knowledge of federal and state tax regulations.',
    requirements: [
      'CPA or JD with tax specialization',
      'Corporate tax planning experience',
      'Restructuring or M&A tax knowledge',
      'Available to start within 2 weeks'
    ],
    skills: ['Tax Advisory', 'Corporate Tax', 'Tax Planning', 'Compliance'],
    postedBy: 'seeded-user-1',
    postedByName: 'Marcus Thompson',
    applications: [
      { userId: 'seeded-user-3', name: 'David Chen', appliedAt: '2026-02-10T09:00:00Z', status: 'accepted' }
    ],
    views: 28,
    status: 'active',
    isActive: true,
    isSeeded: true
  },
  {
    title: 'Directors & Officers Insurance Needed',
    company: 'Growth-Stage Technology Company',
    duration: '2-4 weeks',
    type: 'Contract',
    description: 'Looking for an insurance advisory professional to evaluate and secure Directors & Officers (D&O) liability insurance for a growth-stage technology company preparing for its next funding round. Need comprehensive coverage analysis and broker recommendations.',
    requirements: [
      'D&O insurance expertise',
      'Experience with tech/startup companies',
      'Broker relationships preferred',
      'Understanding of venture-backed company needs'
    ],
    skills: ['D&O Insurance', 'Risk Management', 'Insurance Advisory', 'Corporate Governance'],
    postedBy: 'seeded-user-2',
    postedByName: 'Priya Sharma',
    applications: [
      { userId: 'seeded-user-5', name: 'Robert Martinez', appliedAt: '2026-02-18T11:00:00Z', status: 'pending' }
    ],
    views: 41,
    status: 'active',
    isActive: true,
    isSeeded: true
  },
  {
    title: 'Investment Banking Advisory Needed',
    company: 'Confidential',
    duration: '6-10 weeks',
    type: 'Contract',
    description: 'Seeking an experienced investment banking advisor to support a sell-side M&A process for a mid-market company in the professional services sector. Need assistance with valuation, marketing materials, buyer outreach, and deal negotiation.',
    requirements: [
      'Investment banking or M&A advisory experience',
      'Professional services sector knowledge',
      'Sell-side transaction experience',
      'Strong financial modeling and presentation skills'
    ],
    skills: ['Investment Banking', 'M&A Advisory', 'Valuation', 'Deal Execution'],
    postedBy: 'seeded-user-3',
    postedByName: 'David Chen',
    applications: [],
    views: 19,
    status: 'active',
    isActive: true,
    isSeeded: true
  }
];

// Function to seed all data
export const seedDatabase = async () => {
  try {
    console.log('Starting database seeding...');

    // 1. Seed users with specific IDs
    console.log('Creating sample users...');
    for (const user of sampleUsers) {
      const { id, ...userData } = user;
      await setDoc(doc(db, 'users', id), {
        ...userData,
        uid: id,
        createdAt: new Date('2025-06-15'),
        updatedAt: new Date(),
        memberSince: new Date('2025-06-15')
      });
      console.log(`Created user: ${user.displayName} (${id})`);
    }

    // 2. Clear and seed groups with specific IDs
    console.log('Creating sample groups...');
    for (const group of sampleGroups) {
      try { await deleteDoc(doc(db, 'groups', group.id)); } catch (e) { /* ok */ }
      await setDoc(doc(db, 'groups', group.id), {
        ...group,
        createdAt: new Date('2025-07-01'),
        updatedAt: new Date()
      });
      console.log(`Created group: ${group.name}`);
    }

    // 3. Seed group posts with specific IDs
    console.log('Creating sample group posts...');
    for (const post of sampleGroupPosts) {
      const { id, ...postData } = post;
      await setDoc(doc(db, 'groupPosts', id), {
        ...postData,
        createdAt: new Date('2026-02-10'),
        updatedAt: new Date()
      });
      console.log(`Created post: ${post.title}`);
    }

    // 4. Seed group comments
    console.log('Creating sample group comments...');
    for (const comment of sampleGroupComments) {
      await addDoc(collection(db, 'groupComments'), {
        ...comment,
        createdAt: new Date('2026-02-12'),
        updatedAt: new Date()
      });
      console.log(`Created comment on post: ${comment.postId}`);
    }

    // 5. Seed jobs
    console.log('Creating sample jobs...');
    for (const job of sampleJobs) {
      await addDoc(collection(db, 'jobs'), {
        ...job,
        postedDate: new Date('2026-02-01'),
        createdAt: new Date('2026-02-01'),
        updatedAt: new Date()
      });
      console.log(`Created job: ${job.title}`);
    }

    // 6. Seed projects
    console.log('Creating sample projects...');
    for (const project of sampleProjects) {
      await addDoc(collection(db, 'projects'), {
        ...project,
        postedDate: new Date('2026-02-05'),
        createdAt: new Date('2026-02-05'),
        updatedAt: new Date()
      });
      console.log(`Created project: ${project.title}`);
    }

    console.log('Database seeding completed successfully!');
    return { success: true, users: sampleUsers.length, jobs: sampleJobs.length, projects: sampleProjects.length, posts: sampleGroupPosts.length, comments: sampleGroupComments.length };
  } catch (error) {
    console.error('Error seeding database:', error);
    throw error;
  }
};

// Function to clear all seeded data
export const clearSeededData = async () => {
  try {
    console.log('Clearing all seeded data...');
    let deleted = { users: 0, groups: 0, posts: 0, comments: 0, jobs: 0, projects: 0 };

    // Delete seeded users
    for (const user of sampleUsers) {
      try { await deleteDoc(doc(db, 'users', user.id)); deleted.users++; } catch (e) { /* ok */ }
    }

    // Delete seeded groups
    for (const group of sampleGroups) {
      try { await deleteDoc(doc(db, 'groups', group.id)); deleted.groups++; } catch (e) { /* ok */ }
    }

    // Delete seeded group posts
    for (const post of sampleGroupPosts) {
      try { await deleteDoc(doc(db, 'groupPosts', post.id)); deleted.posts++; } catch (e) { /* ok */ }
    }

    // Delete seeded comments (query by isSeeded flag)
    const commentsQuery = query(collection(db, 'groupComments'), where('isSeeded', '==', true));
    const commentsSnapshot = await getDocs(commentsQuery);
    for (const commentDoc of commentsSnapshot.docs) {
      await deleteDoc(commentDoc.ref);
      deleted.comments++;
    }

    // Delete seeded jobs
    const jobsQuery = query(collection(db, 'jobs'), where('isSeeded', '==', true));
    const jobsSnapshot = await getDocs(jobsQuery);
    for (const jobDoc of jobsSnapshot.docs) {
      await deleteDoc(jobDoc.ref);
      deleted.jobs++;
    }

    // Delete seeded projects
    const projectsQuery = query(collection(db, 'projects'), where('isSeeded', '==', true));
    const projectsSnapshot = await getDocs(projectsQuery);
    for (const projectDoc of projectsSnapshot.docs) {
      await deleteDoc(projectDoc.ref);
      deleted.projects++;
    }

    console.log('Seeded data cleared:', deleted);
    return deleted;
  } catch (error) {
    console.error('Error clearing seeded data:', error);
    throw error;
  }
};

export default {
  sampleUsers,
  sampleGroups,
  sampleGroupPosts,
  sampleGroupComments,
  sampleJobs,
  sampleProjects,
  seedDatabase,
  clearSeededData
};
